/**
 * D2 -- one hook for "what is this IBS's tolerance, and who stands
 * behind it".
 *
 * Four screens described one estate four different ways -- "12 set",
 * "0 declared", "0 of 6 governed", and a dash -- because each computed
 * its own answer from a different field. One composer, one endpoint,
 * one hook.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";

export type ToleranceStatus =
  | "signed"
  | "set_unsigned"
  /** C1: nothing in force, but a declaration is waiting on a signature. */
  | "pending"
  | "undeclared";

export interface ToleranceStateRow {
  ibs_service_id: string;
  ibs_code: string;
  ibs_name: string;
  mvb: boolean;
  hours: number | null;
  status: ToleranceStatus;
  signed_by: string | null;
  /** Round 20 V2 -- legs whose signer does not hold the seat today. */
  signer_seat_lapsed?: {
    leg: string;
    name: string | null;
    seat_at_signing: string | null;
  }[];
  /** Round 20 Y5 -- both signature legs, named and dated, or awaited. */
  legs?: { leg: string; name: string | null; at: string | null }[];
  /** C1: a declaration in flight. Never the number in force -- that is
   *  `hours`, and a proposal must not be shown as though verdicts were
   *  using it. */
  proposed_hours?: number | null;
  awaiting_role?: string | null;
  proposed_by?: string | null;
  proposed_at?: string | null;
  /** Round 14 G -- the declarer's own reason, read back to her. */
  proposed_rationale?: string | null;
  /** ROUND 15 G2 -- since when the awaited leg has been waiting. */
  awaiting_since?: string | null;
  /** Round 16 G14 -- false when the service has no recorded owner. */
  owner_recorded?: boolean;
  /** Round 17 G19 -- who signs the owner's leg. */
  owner_name?: string | null;
}

export interface ToleranceStateOut {
  total: number;
  /** A number exists and drives every breach verdict. */
  in_force: number;
  /** ... and an approved dual sign-off stands behind it. */
  signed: number;
  /** ... and none does. The state most estates are actually in. */
  set_unsigned: number;
  /** No number at all -- nothing can be judged against it. */
  undeclared: number;
  /** Nothing in force, but a declaration is waiting on a signature. */
  pending?: number;
  mvb_total: number;
  mvb_signed: number;
  /** Round 17 P1 -- of `total`, admitted services and functions. */
  admitted_services?: number;
  functions?: number;
  /** Round 18 P11 -- of `functions`, the admitted ones. */
  admitted_functions?: number;
  rows: ToleranceStateRow[];
}

export const toleranceStateKey = ["twin", "tolerance", "state"] as const;

export function useToleranceState(): UseQueryResult<ToleranceStateOut, ApiError> {
  return useQuery({
    queryKey: toleranceStateKey,
    queryFn: () => api.get<ToleranceStateOut>("/v1/twin/tolerance/state"),
    staleTime: 30_000,
  });
}

/** Index by IBS id, for a surface that already has its own rows. */
export function byIbsId(
  data: ToleranceStateOut | undefined,
): Map<string, ToleranceStateRow> {
  const m = new Map<string, ToleranceStateRow>();
  for (const r of data?.rows ?? []) m.set(r.ibs_service_id, r);
  return m;
}
